import { useState, useEffect } from "react";
import {
	Box,
	Typography,
	Card,
	CardContent,
	Grid,
	Chip,
	Button,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Alert,
	Tooltip,
	LinearProgress,
	Link,
	Tab,
	Tabs,
} from "@mui/material";
import {
	Healing as HealingIcon,
	CheckCircle,
	Warning,
	TrendingUp,
	BugReport,
	AutoFixHigh,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatDateTime } from "../utils/dateUtils";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip as RechartsTooltip,
	ResponsiveContainer,
	PieChart,
	Pie,
	Cell,
} from "recharts";
import healingService from "../services/healingService";
import QualityDashboard from "../components/QualityDashboard";

const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444"];

// The Self-healing tab
function HealingAnalytics() {
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState(null);
	const [actionLoading, setActionLoading] = useState(null);
	const { enqueueSnackbar } = useSnackbar();
	const navigate = useNavigate();

	const loadAnalytics = async () => {
		try {
			setLoading(true);
			const result = await healingService.getAnalytics("default");
			setData(result);
		} catch (error) {
			console.error("Failed to load analytics:", error);
			enqueueSnackbar("Failed to load analytics", { variant: "error" });
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadAnalytics();
	}, []);

	const handleApply = async (eventId) => {
		try {
			setActionLoading(eventId);
			await healingService.applyHealing(eventId);
			enqueueSnackbar("Healing applied successfully", {
				variant: "success",
			});
			loadAnalytics();
		} catch (error) {
			enqueueSnackbar("Failed to apply healing", { variant: "error" });
		} finally {
			setActionLoading(null);
		}
	};

	const handleReject = async (eventId) => {
		try {
			setActionLoading(eventId);
			await healingService.rejectHealing(eventId);
			enqueueSnackbar("Healing rejected", { variant: "info" });
			loadAnalytics();
		} catch (error) {
			enqueueSnackbar("Failed to reject healing", { variant: "error" });
		} finally {
			setActionLoading(null);
		}
	};

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (!data) {
		return (
			<Alert severity="info">
				No analytics data available yet. Run some tests to generate
				data.
			</Alert>
		);
	}

	const methodData = [
		{ name: "Fallback", value: data.fallbackCount || 0 },
		{ name: "Similarity", value: data.similarityCount || 0 },
	].filter((d) => d.value > 0);

	const statusData = [
		{ name: "Pending", value: data.pendingReview || 0 },
		{ name: "Applied", value: data.applied || 0 },
		{ name: "Rejected", value: data.rejected || 0 },
	].filter((d) => d.value > 0);

	return (
		<Box>
			{/* Overview Cards */}
			<Grid container spacing={3} sx={{ mb: 4 }}>
				<Grid item xs={12} sm={6} md={3}>
					<Card>
						<CardContent>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mb: 1,
								}}
							>
								<TrendingUp color="primary" />
								<Typography
									variant="body2"
									color="text.secondary"
								>
									Total Executions
								</Typography>
							</Box>
							<Typography variant="h4" fontWeight={700}>
								{data.totalExecutions || 0}
							</Typography>
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<Card>
						<CardContent>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mb: 1,
								}}
							>
								<AutoFixHigh color="success" />
								<Typography
									variant="body2"
									color="text.secondary"
								>
									Healed Executions
								</Typography>
							</Box>
							<Typography variant="h4" fontWeight={700}>
								{data.healedExecutions || 0}
							</Typography>
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<Card>
						<CardContent>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mb: 1,
								}}
							>
								<HealingIcon color="info" />
								<Typography
									variant="body2"
									color="text.secondary"
								>
									Healing Rate
								</Typography>
							</Box>
							<Typography variant="h4" fontWeight={700}>
								{data.healingRate || 0}%
							</Typography>
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<Card>
						<CardContent>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mb: 1,
								}}
							>
								<Warning color="warning" />
								<Typography
									variant="body2"
									color="text.secondary"
								>
									Pending Review
								</Typography>
							</Box>
							<Typography variant="h4" fontWeight={700}>
								{data.pendingReview || 0}
							</Typography>
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			<Grid container spacing={3} sx={{ mb: 4 }}>
				{/* Method Breakdown */}
				{methodData.length > 0 && (
					<Grid item xs={12} md={4}>
						<Card sx={{ height: "100%" }}>
							<CardContent>
								<Typography
									variant="h6"
									fontWeight={600}
									gutterBottom
								>
									Healing Methods
								</Typography>
								<Box sx={{ height: 220 }}>
									<ResponsiveContainer
										width="100%"
										height="100%"
									>
										<PieChart>
											<Pie
												data={methodData}
												cx="50%"
												cy="50%"
												innerRadius={50}
												outerRadius={80}
												paddingAngle={5}
												dataKey="value"
												label={({ name, value }) =>
													`${name}: ${value}`
												}
											>
												{methodData.map(
													(entry, index) => (
														<Cell
															key={entry.name}
															fill={
																COLORS[
																	index %
																		COLORS.length
																]
															}
														/>
													),
												)}
											</Pie>
											<RechartsTooltip />
										</PieChart>
									</ResponsiveContainer>
								</Box>
							</CardContent>
						</Card>
					</Grid>
				)}

				{/* Status Breakdown */}
				{statusData.length > 0 && (
					<Grid item xs={12} md={methodData.length > 0 ? 4 : 6}>
						<Card sx={{ height: "100%" }}>
							<CardContent>
								<Typography
									variant="h6"
									fontWeight={600}
									gutterBottom
								>
									Event Status
								</Typography>
								<Box sx={{ height: 220 }}>
									<ResponsiveContainer
										width="100%"
										height="100%"
									>
										<BarChart data={statusData}>
											<CartesianGrid strokeDasharray="3 3" />
											<XAxis dataKey="name" />
											<YAxis allowDecimals={false} />
											<RechartsTooltip />
											<Bar
												dataKey="value"
												fill="#6366f1"
												radius={[4, 4, 0, 0]}
											>
												{statusData.map(
													(entry, index) => (
														<Cell
															key={entry.name}
															fill={
																[
																	"#f59e0b",
																	"#10b981",
																	"#ef4444",
																][index] ||
																"#6366f1"
															}
														/>
													),
												)}
											</Bar>
										</BarChart>
									</ResponsiveContainer>
								</Box>
							</CardContent>
						</Card>
					</Grid>
				)}

				{/* Top Brittle Tests */}
				<Grid
					item
					xs={12}
					md={
						methodData.length > 0 && statusData.length > 0
							? 4
							: methodData.length > 0 || statusData.length > 0
								? 6
								: 12
					}
				>
					<Card sx={{ height: "100%" }}>
						<CardContent>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mb: 2,
								}}
							>
								<BugReport color="error" />
								<Typography variant="h6" fontWeight={600}>
									Top Brittle Tests
								</Typography>
							</Box>
							{data.topBrittleTests?.length > 0 ? (
								<Box>
									{data.topBrittleTests.map((t, i) => (
										<Box
											key={t.testId}
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												py: 1,
												borderBottom:
													i <
													data.topBrittleTests
														.length -
														1
														? "1px solid"
														: "none",
												borderColor: "divider",
											}}
										>
											<Typography
												variant="body2"
												sx={{
													cursor: "pointer",
													"&:hover": {
														color: "primary.main",
													},
												}}
												onClick={() =>
													navigate(
														`/tests/${t.testId}`,
													)
												}
											>
												{t.testName ||
													t.testId.substring(0, 8) +
														"..."}
											</Typography>
											<Chip
												label={`${t.healCount} heals`}
												size="small"
												color="warning"
												variant="outlined"
											/>
										</Box>
									))}
								</Box>
							) : (
								<Typography
									variant="body2"
									color="text.secondary"
								>
									No brittle tests detected yet.
								</Typography>
							)}
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			{/* Recent Healing Events */}
			<Card>
				<CardContent>
					<Typography variant="h6" fontWeight={600} gutterBottom>
						Recent Healing Events
					</Typography>
					{data.recentEvents?.length > 0 ? (
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Time</TableCell>
										<TableCell>Test</TableCell>
										<TableCell>Step</TableCell>
										<TableCell>Original Locator</TableCell>
										<TableCell>Healed Locator</TableCell>
										<TableCell>Method</TableCell>
										<TableCell>Status</TableCell>
										<TableCell align="right">
											Actions
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{data.recentEvents.map((event) => (
										<TableRow key={event.id}>
											<TableCell>
												<Typography variant="caption">
													{event.createdAt
														? formatDateTime(event.createdAt)
														: "—"}
												</Typography>
											</TableCell>
											<TableCell sx={{ maxWidth: 220 }}>
												{event.testName ? (
													<Tooltip title="Open test">
														<Link
															component="button"
															variant="body2"
															underline="hover"
															onClick={() => navigate(`/tests/${event.testId}`)}
															sx={{ textAlign: "left", fontWeight: 500, display: "block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
														>
															{event.testName}
														</Link>
													</Tooltip>
												) : (
													<Typography variant="body2" color="text.secondary">
														Deleted test
													</Typography>
												)}
											</TableCell>
											<TableCell>
												<Typography
													variant="caption"
													fontFamily="monospace"
												>
													Step #{event.stepId}
												</Typography>
											</TableCell>
											<TableCell>
												<Tooltip
													title={
														event.originalLocatorValue ||
														""
													}
												>
													<Typography
														variant="caption"
														fontFamily="monospace"
														sx={{
															maxWidth: 150,
															overflow: "hidden",
															textOverflow:
																"ellipsis",
															whiteSpace:
																"nowrap",
															display: "block",
														}}
													>
														{
															event.originalLocatorType
														}
														=
														{event.originalLocatorValue?.substring(
															0,
															25,
														)}
													</Typography>
												</Tooltip>
											</TableCell>
											<TableCell>
												<Tooltip
													title={
														event.usedLocatorValue ||
														""
													}
												>
													<Typography
														variant="caption"
														fontFamily="monospace"
														color="success.main"
														sx={{
															maxWidth: 150,
															overflow: "hidden",
															textOverflow:
																"ellipsis",
															whiteSpace:
																"nowrap",
															display: "block",
														}}
													>
														{event.usedLocatorType}=
														{event.usedLocatorValue?.substring(
															0,
															25,
														)}
													</Typography>
												</Tooltip>
											</TableCell>
											<TableCell>
												<Chip
													label={event.method}
													size="small"
													color={
														event.method ===
														"fallback"
															? "primary"
															: "secondary"
													}
													variant="outlined"
												/>
											</TableCell>
											<TableCell>
												<Chip
													label={event.status}
													size="small"
													icon={
														event.status ===
														"APPLIED" ? (
															<CheckCircle />
														) : undefined
													}
													color={
														event.status ===
														"APPLIED"
															? "success"
															: event.status ===
																  "REJECTED"
																? "error"
																: "warning"
													}
												/>
											</TableCell>
											<TableCell align="right">
												{/* Only the test's owner or an admin applies heals */}
												{event.status ===
													"PENDING_REVIEW" &&
													event.canModify !== false && (
													<Box
														sx={{
															display: "flex",
															gap: 0.5,
															justifyContent:
																"flex-end",
														}}
													>
														<Button
															size="small"
															variant="contained"
															color="success"
															disabled={
																actionLoading ===
																event.id
															}
															onClick={() =>
																handleApply(
																	event.id,
																)
															}
														>
															Apply
														</Button>
														<Button
															size="small"
															variant="outlined"
															color="error"
															disabled={
																actionLoading ===
																event.id
															}
															onClick={() =>
																handleReject(
																	event.id,
																)
															}
														>
															Reject
														</Button>
													</Box>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					) : (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ mt: 1 }}
						>
							No healing events yet. When tests use self-healing
							to find elements, events will appear here.
						</Typography>
					)}
				</CardContent>
			</Card>
		</Box>
	);
}

// Analytics: Quality (pass rate, durations, failing / slow / flaky tests) and Self-healing.
// ?tab=healing opens the second tab.
export default function Analytics() {
	const [searchParams, setSearchParams] = useSearchParams();
	const tab = searchParams.get("tab") === "healing" ? "healing" : "quality";
	return (
		<Box>
			<Box sx={{ mb: 2 }}>
				<Typography variant="h4" fontWeight={700}>
					Analytics
				</Typography>
				<Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
					How your tests are doing over time, and what self-healing fixed
				</Typography>
			</Box>
			<Tabs
				value={tab}
				onChange={(_, v) => setSearchParams(v === "healing" ? { tab: "healing" } : {}, { replace: true })}
				sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
			>
				<Tab value="quality" label="Quality" />
				<Tab value="healing" label="Self-healing" />
			</Tabs>
			{tab === "quality" ? <QualityDashboard /> : <HealingAnalytics />}
		</Box>
	);
}
